<template>
  <div class="mega-nav">
    <div
      class="mega-nav__inner container"
      :class="{
        'has-banners': currentItemBanners.length,
      }"
    >
      <div class="mega-nav__columns">
        <template
          v-for="(tier2) of linkItems"
          :key="tier2.handle"
        >
          <ul
            v-if="tier2.links.length"
            class="mega-nav__column"
          >
            <li class="mega-nav__item">
              <a
                class="text-body-m-bold"
                :href="tier2.url"
                v-text="tier2.title"
              />
            </li>

            <li
              v-for="(tier3) of tier2.links"
              :key="tier3.handle"
              class="mega-nav__item"
            >
              <a
                class="mega-nav__link"
                :href="tier3.url"
              >
                <span
                  class="text-body-s-regular"
                  v-text="getLinkText(tier3.title)"
                />

                <span
                  v-if="getLinkTag(tier3.title)"
                  class="mega-nav__tag text-other-label-s"
                  v-text="getLinkTag(tier3.title)"
                />
              </a>
            </li>
          </ul>

          <template v-else>
            <li class="mega-nav__item">
              <a
                class="text-body-s-regular"
                :href="tier2.url"
                v-text="tier2.title"
              />
            </li>
          </template>
        </template>
      </div>

      <div
        v-if="currentItemBanners.length"
        class="mega-nav__banner"
      >
        <div
          v-for="(banner) of currentItemBanners"
          :key="banner.title"
          class="mega-nav__banner-item"
        >
          <a :href="banner.url">
            <responsive-image
              class="mega-nav__banner-image"
              :image="{
                src: getBannerImage(banner)
              }"
              ratio="21:11"
              sizes="448px"
            />

            <div class="mega-nav__banner-content">
              <p
                class="mega-nav__banner-title text-heading-l"
                v-text="getBannerTitle(banner)"
              />

              <p
                class="mega-nav__banner-cta text-other-button-s"
                v-text="$string('general.navigation.shop_all')"
              />
            </div>
          </a>
        </div>
      </div>
    </div>

    <a
      v-if="megaNavFooter?.text"
      class="mega-nav__collectors text-body-s-bold text-center"
      :href="megaNavFooter.url"
      v-text="megaNavFooter.text"
    />
  </div>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Mega nav pane for desktop navigation links.
 * - Component settings: snippet / dynamic / global
 *
 * @param {Array} items - Navigation items, with a handle, title and url.
 *
 */
import ResponsiveImage from '~global/images/responsive-image'

import { convertHandleToSentenceCase } from '~/helpers/convert'
import navigationConfig from '~/config/navigation'

export default {
  name: 'MegaNav',

  components: {
    ResponsiveImage,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    megaNavFooter: {
      type: Object,
      default: () => ({
        text: '',
        url: '',
      }),
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Get link only items.
     * @returns {Array}
     */
    linkItems() {
      return this.items?.filter(
        (item) => !item.title.includes(navigationConfig.bannerCode),
      ) || []
    },

    /**
     * Get current item banners.
     * @returns {Array}
     */
    currentItemBanners() {
      return this.items?.filter(
        (item) => item.title.includes(navigationConfig.bannerCode),
      ) || []
    },
  },

  methods: {

    /**
     * Get parsed link title based on current title.
     * @param {String} title - Link string
     * @returns {String}
     */
    getLinkText(title) {
      if (!title || !title.includes(']')) {
        return title
      }

      return title.split(']').pop()
    },

    /**
     * Get parsed link tag based on current title.
     * @param {String} title - Link string
     * @returns {String}
     */
    getLinkTag(title) {
      const tag = navigationConfig.availableTags.find((tagString) => {
        return title.includes(`[${tagString}]`)
      })

      if (!tag) {
        return false
      }

      const tagHandle = tag.replace('[', '').replace(']', '')
      let tagString = convertHandleToSentenceCase(tagHandle)

      switch (tagHandle) {
        case 'new':
          tagString = this.$string('general.site_header.tags.new')
          break
        default:
          break
      }

      return tagString
    },

    /**
     * Get the collection title from the url.
     * @param {string} url
     * @return {string}
     */
    getBannerTitle(banner) {
      return banner.title.replace(navigationConfig.bannerCode, '').split('|||').pop()
    },

    /**
     * Get banner image.
     * @param {Object} banner - Current banner.
     * @returns {String}
     */
    getBannerImage(banner) {
      return banner.title.replace(navigationConfig.bannerCode, '').split('|||')[0]
    },

    /**
     * @param {string} title
     * @return {boolean}
     */
    isLink(title) {
      return !title.includes(navigationConfig.bannerCode)
    },
  },
}
</script>
